import { AlertService } from 'src/app/shared/store/alert/alert.service';
import { LocalizationService } from 'src/app/services/localization/localization.service';
import { Action, State, StateContext, Store } from '@ngxs/store';
import { HolidaysActions } from './holidays.actions';
import { Injectable } from '@angular/core';
import { patch } from '@ngxs/store/operators';
import { HolidaysService } from '../services/holidays.service';
import { HolidaysForm } from '../models/holidays-form.model';
import { HolidaysResponse, HolidaysSchool, HolidaysSchoolRequest } from '../models/holidays-response.model';
import * as moment from 'moment';
import { MappingUtilsService } from 'src/app/services/utils/mapping-utils.service';

export class HolidaysStateModel {
  holidaysForm: {
    model: HolidaysForm;
  };
  holidaysSchool: HolidaysSchool;
  public isLoadingHolidays: boolean;
  public error: string;
}

const defaults = {
  holidaysForm: {
    model: null,
  },
  holidaysSchool: null,
  isLoadingHolidays: false,
  error: null,
};

@State<HolidaysStateModel>({
  name: 'holidays',
  defaults,
})
@Injectable()
export class HolidaysState {
  constructor(
    private holidaysService: HolidaysService,
    private alertService: AlertService,
    private localizationService: LocalizationService,
    public store: Store
  ) {}

  @Action(HolidaysActions.GetHolidaysSchool)
  getHolidaysSchool(ctx: StateContext<HolidaysStateModel>, { schoolId, exerciseId }: HolidaysActions.GetHolidaysSchool) {
    var state = ctx.getState();
    ctx.patchState({ isLoadingHolidays: true });
    return this.holidaysService.getHolidaysBySchoolId(schoolId, exerciseId).subscribe({
      next: (holidaysSchool) => ctx.dispatch(new HolidaysActions.GetHolidaysSchoolSuccess(holidaysSchool)),
      error: (error) => ctx.dispatch(new HolidaysActions.Error(error)),
    });
  }

  @Action(HolidaysActions.GetHolidaysSchoolSuccess)
  getHolidaysSchoolSuccess(ctx: StateContext<HolidaysStateModel>, { holidaysSchool }: HolidaysActions.GetHolidaysSchoolSuccess) {
    var state = ctx.getState();
    ctx.patchState({
      ...state,
      holidaysSchool: holidaysSchool,
      isLoadingHolidays: false,
    });
  }

  @Action(HolidaysActions.AddHolidays)
  addHolidays(
    ctx: StateContext<HolidaysStateModel>,
    { holidaysForm, user }: HolidaysActions.AddHolidays
  ) {
    var state = ctx.getState();
    let exerciseId = holidaysForm.exerciseId;
    let holidaysResponse = this.getHolidaysResponse(holidaysForm, state);
    let fromDate = moment(holidaysForm.fromDate).format('DD/MM/YYYY');
    let toDate = moment(holidaysForm.toDate).format('DD/MM/YYYY');

    this.alertService.confirm(
      this.localizationService.translate("holidaysAdd.title"),
      this.localizationService.translate('holidaysAdd.holidaysCreateConfirm'),
      this.localizationService.translate('buttons.accept'),
      this.localizationService.translate('buttons.cancel'),
      this.localizationService.translate('holidaysAdd.holidaysCreateConfirm')
      .replace('{0}', fromDate).replace('{1}', toDate)
    ).then((result) => {
      if (result.value) {
        ctx.patchState({ isLoadingHolidays: true });
        return this.holidaysService
          .createHolidays(user, exerciseId, holidaysResponse)
          .subscribe({
            next: () => ctx.dispatch(new HolidaysActions.AddHolidaysSuccess()),
            error: (error) => ctx.dispatch(new HolidaysActions.Error(error)),
          });
      } else {
        return null;
      }
    });
  }

  @Action(HolidaysActions.AddHolidaysSuccess)
  addHolidaysSuccess(
    ctx: StateContext<HolidaysStateModel>,
    {}: HolidaysActions.AddHolidaysSuccess
  ) {
    var state = ctx.getState();
    ctx.patchState({
      ...state,
      isLoadingHolidays: false,
    });
  }

  @Action(HolidaysActions.AddHoliday)
  addHoliday(ctx: StateContext<HolidaysStateModel>, { holidaySchoolRequest, user }: HolidaysActions.AddHoliday) {

    let exerciseId = holidaySchoolRequest.exerciseId;

    this.alertService.confirm(
      this.localizationService.translate("holidaysAdd.title"),
      this.localizationService.translate('holidaysAdd.holidayUpdateConfirm'),
      this.localizationService.translate('buttons.accept'),
      this.localizationService.translate('buttons.cancel'),
      this.localizationService.translate('holidaysAdd.holidayUpdateConfirm')
    ).then((result) => {
      if (result.value) {
        ctx.patchState({ isLoadingHolidays: true });
        return this.holidaysService
          .updateHolidays(user, exerciseId, holidaySchoolRequest)
          .subscribe({
            next: () => ctx.dispatch(new HolidaysActions.AddHolidaySuccess()),
            error: (error) => ctx.dispatch(new HolidaysActions.Error(error)),
          });
      } else {
        return null;
      }
    });
  }

  @Action(HolidaysActions.AddHolidaySuccess)
  addHolidaySuccess(ctx: StateContext<HolidaysStateModel>, {}: HolidaysActions.AddHolidaySuccess) {
    var state = ctx.getState();
    ctx.patchState({
      ...state,
      isLoadingHolidays: false,
    });
  }

  @Action(HolidaysActions.DeleteHolidays)
  deleteHolidays(
    ctx: StateContext<HolidaysStateModel>,
    { holidaysForm, user }: HolidaysActions.DeleteHolidays
  ) {
    var state = ctx.getState();
    let exerciseId = holidaysForm.exerciseId;
    let holidaysResponse = this.getHolidaysResponse(holidaysForm, state);
    let fromDate = moment(holidaysForm.fromDate).format('DD-MM-YYYY');
    let toDate = moment(holidaysForm.toDate).format('DD-MM-YYYY');

    this.alertService.confirm(
      this.localizationService.translate("holidaysAdd.title"),
      this.localizationService.translate('holidaysAdd.holidaysDeleteConfirm'),
      this.localizationService.translate('buttons.accept'),
      this.localizationService.translate('buttons.cancel'),
      this.localizationService.translate('holidaysAdd.holidaysDeleteConfirm')
      .replace('{0}', fromDate).replace('{1}', toDate)
    ).then((result) => {
      if (result.value) {
        ctx.patchState({ isLoadingHolidays: true });
        return this.holidaysService
          .deleteHolidays(user, exerciseId, holidaysResponse)
          .subscribe({
            next: () => ctx.dispatch(new HolidaysActions.DeleteHolidaysSuccess()),
            error: (error) => ctx.dispatch(new HolidaysActions.Error(error)),
          });
      } else {
        return null;
      }
    });
  }

  @Action(HolidaysActions.DeleteHolidaysSuccess)
  deleteHolidaysSuccess(
    ctx: StateContext<HolidaysStateModel>,
    {}: HolidaysActions.DeleteHolidaysSuccess
  ) {
    var state = ctx.getState();
    ctx.patchState({
      ...state,
      isLoadingHolidays: false,
    });
  }

  @Action(HolidaysActions.Error)
  onErrorOcurred(
    ctx: StateContext<HolidaysStateModel>,
    { error }: HolidaysActions.Error
  ) {
      ctx.patchState({
        error: error,
        isLoadingHolidays: false,
      });
      MappingUtilsService.showError(this.getTitle(), error, this.alertService, this.localizationService);
  }

  @Action(HolidaysActions.SetHolidaysDefaultState)
  setHolidaysDefaultState(
    ctx: StateContext<HolidaysStateModel>,
    {}: HolidaysActions.SetHolidaysDefaultState
  ) {
    ctx.setState(
      patch({
        holidaysForm: patch({
          model: defaults.holidaysForm.model,
        }),
      })
    );
  }

  private getHolidaysResponse(
    holidaysForm: HolidaysForm,
    state: HolidaysStateModel
  ) {
    let holidaysResponse = new HolidaysResponse();

    holidaysResponse.zoneId = holidaysForm.zoneId != -1 ? holidaysForm.zoneId : null;
    holidaysResponse.profitCenterCode = holidaysForm.profitCenterCode != "-1" ? holidaysForm.profitCenterCode : "";
    holidaysResponse.schoolId = holidaysForm.schoolId != -1 ? holidaysForm.schoolId : null;
    holidaysResponse.fromDate = moment(holidaysForm.fromDate).format(
      'YYYY-MM-DD'
    );
    holidaysResponse.toDate = moment(holidaysForm.toDate).format('YYYY-MM-DD');

    return holidaysResponse;
  }

  private getTitle() {
    return this.localizationService.translate('holidays.title');
  }
}
